import React from 'react'
import { useAutofocus } from 'react-autofocus'
import { useTimer } from 'react-timer'
import { Editor, EditorState } from 'draft-js'
import { Language, Link, LocalizedString, Media, SVGImage } from '~/models'
import { languagesStore } from '~/stores'
import { LinkFormModel } from '~/ui/app/links'
import MediaUploader, { MediaUploaderState } from '~/ui/app/media/MediaUploader'
import { observer } from '~/ui/component'
import { childFlex, HBox, LanguageToggleButton, Scroller, VBox, VBoxProps } from '~/ui/components'
import MarkdownField from '~/ui/components/fields/MarkdownField'
import { useMarkdownStyles } from '~/ui/components/markdown'
import { FieldChangeCallback, invokeFieldChangeCallback } from '~/ui/form'
import { useContinuousRef, useFormOpen, usePrevious, useViewState } from '~/ui/hooks'
import { createUseStyles, fonts, layout, presets, shadows } from '~/ui/styling'
import LinkForm from '../../links/form/LinkForm'
import DraftJSBackend from './backend/DraftJSBackend'
import MarkdownBackend from './backend/MarkdownBackend'
import renderBlock from './blocks'
import { RichTextFieldContext } from './RichTextFieldContext'
import RichTextFieldToolbar from './RichTextFieldToolbar'
import { EditorMode, RichTextFieldHandle, RichTextNodeType, RichTextScope } from './types'

export interface Props {
  value:        string | LocalizedString | null
  onChange?:    ((value: string | LocalizedString) => any) | FieldChangeCallback<string | LocalizedString>
  onCommit?:    (value: string | LocalizedString) => any

  scope?: RichTextScope
  allowedNodes?: RichTextNodeType[] | '*'

  enabled?:       boolean
  localized?:     boolean
  invalid?:       boolean
  autoFocus?:     boolean
  selectOnFocus?: boolean
  commitOnBlur?:  boolean
  placeholder?:   string | LocalizedString | null

  renderToolbarRight?: () => React.ReactNode
  renderHeader?:       () => React.ReactNode
  renderFooter?:       () => React.ReactNode

  flex?:            VBoxProps['flex']
  height?:          number
  maxHeight?:       number

  borderTopRadius?: number
  classNames?:      React.ClassNamesProp
  bodyClassNames?:  React.ClassNamesProp
  scrollable?:      boolean
  showFocus?:       boolean
}

const RichTextField = observer('RichTextField', React.forwardRef((props: Props, ref: React.Ref<RichTextFieldHandle>) => {

  const {
    value,
    onChange,
    onCommit,

    scope = 'block',
    allowedNodes = '*',

    enabled       = true,
    localized     = false,
    invalid       = false,
    autoFocus     = false,
    selectOnFocus = false,
    commitOnBlur  = false,
    placeholder,

    flex,
    height,
    maxHeight,
    scrollable,

    borderTopRadius = presets.fieldBorderRadius,

    renderToolbarRight: props_renderToolbarRight,
    renderHeader,
    renderFooter,
    showFocus = true,

    classNames,
    bodyClassNames,
  } = props

  const valueRef            = useContinuousRef(value)
  const editorRef           = React.useRef<Editor>(null)
  const markdownTextAreaRef = React.useRef<HTMLTextAreaElement>(null)
  const uploaderRef         = React.useRef<MediaUploader>(null)

  // This prevents the DraftJS backend from being recreated on every render
  const allowedNodesRef = useContinuousRef(allowedNodes)

  const contentFlex = childFlex(flex)

  // #region Languages

  const availableLanguages = languagesStore.availableLanguages
  const [language, setLanguage] = React.useState<string>(languagesStore.defaultLanguage)

  const currentDefaultLanguage = languagesStore.defaultLanguage
  const prevDefaultLanguage = usePrevious(languagesStore.defaultLanguage)
  React.useEffect(() => {
    if (prevDefaultLanguage === undefined) { return }
    if (currentDefaultLanguage === prevDefaultLanguage) { return }
    setLanguage(currentDefaultLanguage)
  }, [currentDefaultLanguage, prevDefaultLanguage])

  // #endregion

  //------
  // Backends

  const [editorMode, state_setEditorMode] = useViewState<EditorMode>('rich-text-field.mode', 'wysiwyg')

  const languageRef = useContinuousRef(language)
  const onChangeRef = useContinuousRef(onChange)
  const onCommitRef = useContinuousRef(onCommit)

  const onChangeForBackend = React.useCallback((value: string, commit: boolean) => {
    if (localized) {
      const nextValue = LocalizedString.replace(valueRef.current as LocalizedString, languageRef.current, value)
      invokeFieldChangeCallback(onChangeRef.current, nextValue, !commit)
    } else {
      invokeFieldChangeCallback(onChangeRef.current, value, !commit)
    }
  }, [languageRef, localized, onChangeRef, valueRef])

  const onCommitForBackend = React.useCallback((value: string) => {
    if (localized) {
      const nextValue = LocalizedString.replace(valueRef.current as LocalizedString, languageRef.current, value)
      onCommitRef.current?.(nextValue)
    } else {
      onCommitRef.current?.(value)
    }
  }, [languageRef, localized, onCommitRef, valueRef])

  const createBackend = React.useCallback((text: string) => {
    if (editorMode === 'wysiwyg') {
      return new DraftJSBackend(text, scope, onChangeForBackend, onCommitForBackend, allowedNodesRef.current)
    } else {
      return new MarkdownBackend(text, markdownTextAreaRef, scope, onChangeForBackend, onCommitForBackend)
    }
  }, [editorMode, onChangeForBackend, onCommitForBackend, scope, allowedNodesRef])

  const backends = React.useMemo(() => {
    const backends = new Map<Language | null, DraftJSBackend | MarkdownBackend>()

    if (localized) {
      for (const [language, text] of Object.entries(valueRef.current)) {
        backends.set(language, createBackend(text))
      }
      for (const language of availableLanguages) {
        if (!backends.has(language.code)) {
          backends.set(language.code, createBackend(''))
        }
      }
    } else {
      backends.set(null, createBackend(valueRef.current as string ?? ''))
    }
    return backends
  }, [availableLanguages, createBackend, localized, valueRef])

  const backend = React.useMemo(() => {
    let backend: DraftJSBackend | MarkdownBackend | undefined
    if (localized) {
      backend = backends.get(language)
    } else {
      backend = backends.get(null)
    }
    if (backend == null) {
      throw new Error(`Backend for '${language}' cannot be null`)
    }
    return backend
  }, [backends, language, localized])

  // For some reason, MobX doesn't get into action unless we try to get this.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ = backend instanceof DraftJSBackend ? backend.editorState : null

  React.useEffect(() => {
    if (localized) {
      for (const [language, text] of Object.entries(value)) {
        backends.get(language)?.updateFromValue(text)
      }
    } else {
      backends.get(null)?.updateFromValue(value as string ?? '')
    }
  }, [backend, backends, localized, value])

  const handleMarkdownChange = React.useCallback((value: string) => {
    if (!(backend instanceof MarkdownBackend)) { return }
    backend.handleChange(value)
  }, [backend])

  const handleMarkdownSelectionChange = React.useCallback(() => {
    if (!(backend instanceof MarkdownBackend)) { return }
    backend.handleSelectionChange()
  }, [backend])

  const handleDraftJSChange = React.useCallback((state: EditorState) => {
    if (!(backend instanceof DraftJSBackend)) { return }
    backend.handleChange(state)
  }, [backend])

  //------
  // Focus & select

  const selectAll = React.useCallback(() => backend.selectAll(), [backend])

  const focus = React.useCallback(() => {
    editorRef.current?.focus()

    if (selectOnFocus) {
      selectAll()
    }
  }, [selectAll, selectOnFocus])

  const blur = React.useCallback(() => {
    editorRef.current?.blur()
  }, [])

  const preventBlurRef = React.useRef<boolean>(false)
  const timer = useTimer()

  const mouseDown = React.useCallback(() => {
    preventBlurRef.current = true
    timer.setTimeout(() => {
      preventBlurRef.current = false
    }, 0);
  }, [timer])

  const performAutoFocus = React.useCallback(() => {
    if (autoFocus) {
      focus()
    }
  }, [autoFocus, focus])

  useAutofocus(performAutoFocus)

  const handleBlur = React.useCallback((event: React.FocusEvent) => {
    if (preventBlurRef.current) {
      event.preventDefault()
    } else if (commitOnBlur) {
      onCommit?.(value ?? '')
    }
  }, [commitOnBlur, onCommit, value])

  React.useImperativeHandle(ref, () => ({
    focus,
    blur,
    selectAll,
  }))

  //------
  // Media & links

  const acceptLinks = allowedNodes === '*' || allowedNodes.includes('link')

  const [linkFormModel, setLinkFormModel] = React.useState<LinkFormModel | null>(null)
  const [linkFormOpen, currentLinkFormModel, closeLinkForm] = useFormOpen(linkFormModel, () => { setLinkFormModel(null) })

  const insertMedia = React.useCallback((media: Media | SVGImage | null) => {
    if (media instanceof Media) {
      backend.insertMedia(media)
    }
  }, [backend])

  const insertLink = React.useCallback((link: Link | null, caption: string | null) => {
    if (link == null) {
      return Promise.resolve(undefined)
    } else {
      return backend.insertLink(link, caption)
    }
  }, [backend])

  const acceptMedia = scope === 'block' && (allowedNodes === '*' || allowedNodes.includes('image'))

  const openMediaPicker = React.useCallback(() => {
    uploaderRef.current?.browse()
  }, [])

  const openLinkForm = React.useCallback(() => {
    const model = new LinkFormModel(insertLink, null)
    preventBlurRef.current = true
    setLinkFormModel(model)
  }, [insertLink])

  //------
  // Widgets

  const insertWidget = React.useCallback((widget: string) => {
    backend.insertWidget(widget)
  }, [backend])

  //------
  // Mode

  const setEditorMode = React.useCallback((mode: EditorMode) => {
    state_setEditorMode(mode)
    if (mode === 'wysiwyg') {
      timer.setTimeout(() => {
        focus()
      }, 0)
    }
  }, [focus, state_setEditorMode, timer])

  //------
  // Modals

  const [modals, setModals] = React.useState<Map<any, React.ReactNode>>(new Map())
  const modalsRef = useContinuousRef(modals)

  const setModalsForHandle = React.useCallback((handle: any, modals: React.ReactNode) => {
    const nextModals = new Map(modalsRef.current)
    nextModals.set(handle, modals)
    setModals(modalsRef.current = nextModals)

    return () => {
      const nextModals = new Map(modalsRef.current)
      nextModals.delete(handle)
      setModals(modalsRef.current = nextModals)
    }
  }, [modalsRef])

  //------
  // Context

  const context = React.useMemo((): RichTextFieldContext => ({
    backend,
    setModals: setModalsForHandle,
  }), [backend, setModalsForHandle])

  //------
  // Rendering

  const $ = useStyles({height, maxHeight})
  const markdown$ = useMarkdownStyles()

  const borderRadiusStyle: React.CSSProperties = {
    borderTopLeftRadius:  borderTopRadius,
    borderTopRightRadius: borderTopRadius,
  }

  function render() {
    return (
      <VBox flex={flex} classNames={[$.RichTextField, {invalid, showFocus}, classNames]} style={borderRadiusStyle}>
        {acceptMedia ? (
          renderDropzone()
        ) : (
          renderEditor()
        )}
        {renderLinkForm()}
        {Array.from(modals.values()).map((modal, index) => (
          <React.Fragment key={index}>
            {modal}
          </React.Fragment>
        ))}
      </VBox>
    )
  }

  function renderDropzone() {
    if (!acceptMedia) { return null }

    return (
      <MediaUploader
        ref={uploaderRef}
        accept={['image/jpeg', 'image/png', 'image/gif']}
        renderContent={renderDropzoneContent}
        onUploadComplete={insertMedia}
        noKeyboard
        noClick
      />
    )
  }

  function renderDropzoneContent(state: MediaUploaderState) {
    return (
      <VBox flex={flex} classNames={$.dropzoneContent}>
        {renderEditor()}
        {state.isDragActive && state.renderDropHint()}
        {state.renderUploading()}
      </VBox>
    )
  }

  function renderEditor() {
    return (
      <RichTextFieldContext.Provider value={context}>
        <VBox flex={contentFlex} onMouseDown={mouseDown} className={$.container}>
          <VBox classNames={$.toolbar} style={borderRadiusStyle}>
            {renderToolbar()}
          </VBox>
          {renderHeader?.()}
          <VBox flex={contentFlex} classNames={[$.body, markdown$.markdown, bodyClassNames]}>
            {scrollable ? (
              <Scroller flex={contentFlex}>
                {renderEditorBody()}
              </Scroller>
            ) : (
              renderEditorBody()
            )}
          </VBox>
          {renderFooter?.()}
        </VBox>
      </RichTextFieldContext.Provider>
    )
  }

  function renderToolbar() {
    const headingButtons = scope === 'block' && (allowedNodes === '*' || allowedNodes.includes('heading'))
    const listButtons    = scope === 'block' && (allowedNodes === '*' || allowedNodes.includes('list'))
    const acceptWidgets  = scope === 'block' && (allowedNodes === '*' || allowedNodes.includes('widget'))

    return (
      <RichTextFieldToolbar
        editorMode={editorMode}
        setEditorMode={setEditorMode}
        renderRight={renderToolbarRight}
        scope={scope}
        requestInsertMedia={acceptMedia ? openMediaPicker : undefined}
        requestInsertLink={acceptLinks ? openLinkForm : undefined}
        headingButtons={headingButtons}
        listButtons={listButtons}

        widgets={acceptWidgets ? ['signup', 'answers'] : undefined}
        requestInsertWidget={acceptWidgets ? insertWidget : undefined}
      />
    )
  }

  function renderToolbarRight() {
    const upstream = props_renderToolbarRight?.()
    const languageToggle = localized ? (
      <LanguageToggleButton
        value={language}
        languages={availableLanguages}
        onChange={setLanguage}
        showLanguage={false}
        small
      />
    ) : null
    if (upstream == null && languageToggle == null) { return null }

    return (
      <HBox>
        {languageToggle}
        {upstream}
      </HBox>
    )
  }

  function renderLinkForm() {
    if (!acceptLinks || currentLinkFormModel == null) { return null }

    return (
      <LinkForm
        open={linkFormOpen}
        requestClose={closeLinkForm}
        model={currentLinkFormModel}
      />
    )
  }

  function renderEditorBody() {
    if (backend instanceof DraftJSBackend) {
      return (
        <Editor
          ref={editorRef}
          editorState={backend.editorState}
          keyBindingFn={backend.keyBindingFn}
          handleKeyCommand={backend.keyCommandHandler}
          blockRendererFn={renderBlock}
          onChange={handleDraftJSChange}
          onBlur={handleBlur}
          readOnly={!enabled}
          placeholder={LocalizedString.translate(placeholder)}
        />
      )
    } else {
      return(
        <MarkdownField
          classNames={$.markdownEditor}
          ref={markdownTextAreaRef}
          value={backend.value}
          onChange={handleMarkdownChange}
          onCommit={onCommit}
          inputAttributes={{
            onSelect: handleMarkdownSelectionChange,
          }}
          enabled={enabled}
          autoFocus
        />
      )
    }
  }

  return render()

}))

export default RichTextField

export const minHeight = 80

const useStyles = createUseStyles(theme => ({
  RichTextField: {
    ...presets.field(theme),
    ...fonts.responsiveFontStyle(theme.fonts.body),

    background: 'red',

    fontWeight: 400,
    cursor:     'text',
    padding:    0,
    overflow:   'hidden',

    '&.invalid': {
      ...presets.invalidField(theme),
    },
    '&:not(.showFocus)': {
      '&, &:focus-within': {
        outline:   'none',
        boxShadow: 'none',
      },
    },
  },

  container: {
    flex: [1, 1, 'auto'],
  },

  dropzoneContent: {
    position: 'relative',
  },

  markdownEditor: ({height = minHeight, maxHeight}: any) => ({
    flex: [1, 1, 'auto'],
    minHeight,
    maxHeight,
    height,
    // $.richtTextField has field styles already
    ...presets.unstyledField(theme),
  }),

  body: ({height = minHeight, maxHeight}: any) => ({
    flex: [1, 1, 'auto'],
    '& .DraftEditor-root': {
      flex: [1, 1, 'auto'],
      minHeight,
      maxHeight,
      height,
      overflow: 'auto',
    },

    '& .public-DraftEditor-content': {
      padding: layout.padding.inline.l,
    },

    '& .DraftEditor-editorContainer, & .public-DraftEditor-content': {
      height: '100%',
    },

    '& .public-DraftEditorPlaceholder-root': {
      position:   'absolute',
      color:      theme.fg.placeholder,
      fontWeight: 400,
    },

    // Markdown works with paragraphs, DraftJS works with blocks. Apply paragraph styles to blocks
    // to ensure the same spacing.
    '& [data-block]:not(:first-child):not(ol):not(ul):not(li)': {
      marginTop: '1.2em',
    },
  }),

  toolbar: {
    position:  'relative',
    zIndex:    1, // To place above shadow

    background: theme.fg.dimmer,
    cursor:     'default',

    '$RichTextField:focus-within &': {
      boxShadow:  [0, 3, 2, -2, shadows.shadowColor.alpha(0.2)],
      background: theme.semantic.primary,
    },
  },
}))